// Trigger collector for `on_enter_location`. When a unit lands on a
// cell, which OTHER cards in play should fire their entry?
//
// Two firing modes:
//
//   Pass 1. Cards AT the entered cell. Covers intrinsic
//   `on_enter_location` entries on sites/artifacts/units sitting on
//   the cell, plus the `_grantedTriggers[]` installed by static auras
//   (Druid's back face grants `on_enter_location → damage_watch_target`
//   onto nearby allied sites).
//
//   Pass 2. Auras whose `_auraAnchor` + `_auraShape` covered set
//   includes the entered cell, BUT whose own mesh isn't on that cell.
//   This is the Mariner's Curse / Rest in Peace shape. The aura
//   watches every cell it covers, not just the one its mesh happens
//   to sit at. Cards on the aura's anchor cell ARE the aura's mesh
//   for ATOP_SITE, so to avoid double-firing we exclude any aura
//   already caught in Pass 1.
//
// Pure. No GameBoard state. The dispatcher (GameBoard or anywhere
// else) runs each returned firing through whatever runtime it uses.

import { anchorCoveredCells } from '../../core/auraPlacement';
import { isCardSilenced } from '../../core/keywordQuery';
import { isDisabled } from '../scope';
import { TRIGGER } from '../schema';

/**
 * Collect every (card, fullCard, entries) tuple that should fire its
 * `on_enter_location` trigger because `enteringUnit` just landed on
 * `cell`.
 *
 * @param {object} enteringUnit
 * @param {{ col: number, row: number }} cell
 * @param {Iterable<[any, { userData: { cardInstance }, position: { x, z } }]>} meshes
 * @param {(x: number, z: number) => { col, row } | null} cellAt
 * @param {(cardId: string) => object | null} getFullCard
 * @returns {{ card: object, fullCard: object, entries: object[] }[]}
 */
export function collectOnEnterLocationFirings(enteringUnit, cell, meshes, cellAt, getFullCard) {
  if (!enteringUnit || !cell) return [];
  const enteredKey = `${cell.col},${cell.row}`;
  const out = [];
  const visited = new Set();

  // Pass 1. Cards AT the entered cell.
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.id === enteringUnit.id) continue;
    // Silenced/disabled cards have no box text, so they don't fire any
    // on_enter_location trigger (intrinsic OR granted) of their own.
    if (isCardSilenced(card) || isDisabled(card)) continue;
    const cardCell = cellAt(mesh.position.x, mesh.position.z);
    if (!cardCell || cardCell.col !== cell.col || cardCell.row !== cell.row) continue;
    const fullCard = getFullCard(card.cardId);
    const intrinsic = (fullCard?.automation || []).filter((e) => e.trigger === TRIGGER.ON_ENTER_LOCATION);
    const granted = (card._grantedTriggers || []).filter((e) => e.trigger === TRIGGER.ON_ENTER_LOCATION);
    if (intrinsic.length === 0 && granted.length === 0) continue;
    out.push({ card, fullCard, entries: [...intrinsic, ...granted] });
    visited.add(card.id);
  }

  // Pass 2. Auras whose covered cells include the entered cell, but
  // whose own anchor cell isn't the entered cell (those are already in
  // Pass 1). Auras themselves can't be silenced (Silence only filters
  // on Minion), but check anyway in case future cards extend the
  // silencing scope.
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.type !== 'Aura') continue;
    if (visited.has(card.id)) continue;
    if (isCardSilenced(card) || isDisabled(card)) continue;
    if (!card._auraShape || !card._auraAnchor) continue;
    const covered = new Set(anchorCoveredCells(card._auraAnchor, card._auraShape.shape));
    if (!covered.has(enteredKey)) continue;
    const fullCard = getFullCard(card.cardId);
    const intrinsic = (fullCard?.automation || []).filter((e) => e.trigger === TRIGGER.ON_ENTER_LOCATION);
    if (intrinsic.length === 0) continue;
    out.push({ card, fullCard, entries: intrinsic });
  }

  return out;
}
